import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import footerLogo from '../assets/about/Landmark Construction Logo.jpg.jpeg';
import { scrollToTopNow } from './ScrollToTop';
import './Footer.css';

const quickLinks = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/projects', label: 'Projects' },
  { to: '/services', label: 'Services' },
  { to: '/blog', label: 'Blog' },
  { to: '/contact', label: 'Contact' },
  { to: '/privacy', label: 'Privacy Policy' },
  { to: '/terms', label: 'Terms & Conditions' },
];

function IconMail() {
  return (
    <svg className="footer-contact-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 6h16v12H4V6zm0 0l8 6 8-6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconPhone() {
  return (
    <svg className="footer-contact-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6.5 4.5h3l1.5 4.5-2 1.5c1.2 2.4 3.6 4.8 6 6l1.5-2 4.5 1.5v3c0 .8-.7 1.5-1.5 1.5C8.6 20 4 15.4 4 9.5c0-.8.7-1.5 1.5-1.5z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconPin() {
  return (
    <svg className="footer-contact-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 21s7-4.35 7-11a7 7 0 10-14 0c0 6.65 7 11 7 11z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="10" r="2.25" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function SocialFacebook() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M13.5 22v-8.5h2.8l.4-3.2H13.5V8.9c0-.9.25-1.5 1.6-1.5h1.7V4.3c-.8-.1-1.7-.1-2.6-.1-2.6 0-4.4 1.6-4.4 4.5v2.5H7v3.2h2.7V22h3.8z" />
    </svg>
  );
}

function SocialInstagram() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M7 2h10a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5zm0 2a3 3 0 00-3 3v10a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H7zm5 3.5a5.5 5.5 0 110 11 5.5 5.5 0 010-11zm0 2a3.5 3.5 0 100 7 3.5 3.5 0 000-7zM17.5 6.5a1 1 0 110 2 1 1 0 010-2z" />
    </svg>
  );
}

function SocialLinkedIn() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M6.5 8.5h-3V21h3V8.5zm-1.5-4.2c-1 0-1.7.8-1.7 1.7 0 1 .8 1.7 1.7 1.7s1.7-.8 1.7-1.7c0-.9-.8-1.7-1.7-1.7zM21 21h-3v-5.6c0-1.3-.5-2.2-1.6-2.2-.9 0-1.4.6-1.6 1.2V21h-3V8.5h3v1.3s1-1.5 2.8-1.5c2.4 0 3.4 1.6 3.4 4.5V21z" />
    </svg>
  );
}

export default function Footer() {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const { pathname } = useLocation();

  const handleNewsletter = (e) => {
    e.preventDefault();
    setNewsletterEmail('');
  };

  const handleLinkClick = (to) => {
    const targetPath = to.split('#')[0] || '/';
    if (pathname === targetPath || (targetPath === '/' && pathname === '/')) {
      scrollToTopNow(true);
    }
  };

  const year = new Date().getFullYear();

  return (
    <footer className="site-footer">
      <div className="site-footer-grid">
        <div className="footer-col footer-col-brand">
          <Link to="/" className="footer-logo-link" onClick={() => handleLinkClick('/')}>
            <span className="footer-logo-panel">
              <img src={footerLogo} alt="Landmarks Construction" className="footer-logo-img" width={355} height={207} />
            </span>
          </Link>
          <p className="footer-about">
            Landmarks Construction was founded on January 19, 2017, in Noida and serves clients
            throughout Delhi NCR with trusted building, renovation, and project delivery. We help
            homeowners and businesses plan with clarity and execute with confidence.
          </p>
          <div className="footer-badges">
            <span className="footer-badge">RERA Approved</span>
            <span className="footer-badge">ISO-Certified</span>
          </div>
        </div>

        <div className="footer-col">
          <h3 className="footer-heading">Quick Links</h3>
          <ul className="footer-links">
            {quickLinks.map(({ to, label }) => (
              <li key={to + label}>
                <Link to={to} className="footer-link" onClick={() => handleLinkClick(to)}>
                  <span className="footer-chevron" aria-hidden>
                    ›
                  </span>
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="footer-col">
          <h3 className="footer-heading">Contact Us</h3>
          <ul className="footer-contact-list">
            <li>
              <IconMail />
              <a href="mailto:info@landmarksconstruction.in">info@landmarksconstruction.in</a>
            </li>
            <li>
              <IconPhone />
              <a href="tel:+919319219252">+91-93192-19252</a>
            </li>
            <li>
              <IconPin />
              <span>
                G-43, 3rd Floor, Sector 3, Noida – 201303, Uttar Pradesh, India
              </span>
            </li>
          </ul>
        </div>

        <div className="footer-col footer-col-news">
          <h3 className="footer-heading">Stay in the Loop</h3>
          <p className="footer-news-text">
            Get updates on new projects, site progress, and limited-time opportunities.
          </p>
          <form className="footer-news-form" onSubmit={handleNewsletter}>
            <div className="footer-news-input-wrap">
              <input
                type="email"
                name="newsletter"
                placeholder="Enter email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                autoComplete="email"
                aria-label="Email for newsletter"
              />
              <button type="submit" className="footer-news-join">
                Join
              </button>
            </div>
          </form>
          <div className="footer-social" aria-label="Social media">
            <a
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-social-btn"
              aria-label="Facebook"
            >
              <SocialFacebook />
            </a>
            <a
              href="https://www.instagram.com/landmarksconstructionrealty?igsh=MWdzaTc1ejV3YWY5ZQ%3D%3D&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-social-btn"
              aria-label="Instagram"
            >
              <SocialInstagram />
            </a>
            <a
              href="https://www.linkedin.com/in/landmarks-construction-realty-601785404/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-social-btn"
              aria-label="LinkedIn"
            >
              <SocialLinkedIn />
            </a>
          </div>
        </div>
      </div>

      <div className="footer-divider" />
      <div className="footer-bottom">
        <p>© {year} Landmarks Construction — All Rights Reserved.</p>
      </div>
    </footer>
  );
}
